export type VoieKey =
  | 'comprime'
  | 'sirop'
  | 'suppositoire'
  | 'injection'
  | 'creme'
  | 'aerosol'
  | 'gouttes'
  | 'sublingual'
  | 'patch'
  | 'capsule'
  | 'poudre'
  | 'gel'
  | 'pastille'
  | 'ovule';

export type DosageType = 'count' | 'ml' | 'text';

export type Conditionnement = 'boite' | 'plaquette';

export interface VoieMedicament {
  key: VoieKey;
  groupe: string;
  label: string;
  voieText: string;
  unite: string;
  dosageType: DosageType;
  dosageLabel: string;
  dosagePlaceholder?: string;
  showBoites: boolean;
  showSiteApplication?: boolean;
  showVoieInjection?: boolean;
  showSiteGouttes?: boolean;
  showDissolution?: boolean;
  defaultConditionnement?: Conditionnement;
}


export interface MedicamentLigne {
  id: string;
  nom: string;
  dosageNb: string;
  forme: VoieKey;
  matin: boolean;
  midi: boolean;
  soir: boolean;
  nbBoites: string;
  conditionnement?: Conditionnement;
  instructions: string;
  duree: string;
  voieInjection?: string;
  siteApplication?: string;
  siteGouttes?: string;
  dissolution?: string;
}

export const VOIES_MEDICAMENT: VoieMedicament[] = [
  {
    key: 'comprime',
    groupe: 'Voie orale',
    label: 'Comprimé',
    voieText: 'à avaler',
    unite: 'comprimé',
    dosageType: 'count',
    dosageLabel: 'Nombre de comprimés',
    showBoites: true,
    defaultConditionnement: 'plaquette',
  },
  {
    key: 'capsule',
    groupe: 'Voie orale',
    label: 'Capsule / gélule',
    voieText: 'à avaler',
    unite: 'capsule',
    dosageType: 'count',
    dosageLabel: 'Nombre de capsules',
    showBoites: true,
    defaultConditionnement: 'plaquette',
  },
  {
    key: 'sublingual',
    groupe: 'Voie orale',
    label: 'Comprimé sublingual',
    voieText: 'à placer sous la langue',
    unite: 'comprimé',
    dosageType: 'count',
    dosageLabel: 'Nombre de comprimés',
    showBoites: true,
    defaultConditionnement: 'plaquette',
  },
  {
    key: 'pastille',
    groupe: 'Voie orale',
    label: 'Pastille / comprimé à sucer',
    voieText: 'à sucer (bouche / gorge)',
    unite: 'pastille',
    dosageType: 'count',
    dosageLabel: 'Nombre de pastilles',
    showBoites: true,
    defaultConditionnement: 'plaquette',
  },
  {
    key: 'poudre',
    groupe: 'Voie orale',
    label: 'Poudre / granulés',
    voieText: 'à dissoudre ou à prendre directement',
    unite: 'sachet',
    dosageType: 'count',
    dosageLabel: 'Nombre de sachets',
    showBoites: true,
    showDissolution: true,
  },
  {
    key: 'sirop',
    groupe: 'Voie orale',
    label: 'Sirop / solution buvable',
    voieText: 'liquide à boire',
    unite: 'ml',
    dosageType: 'ml',
    dosageLabel: 'Quantité (ml)',
    showBoites: false,
  },
  {
    key: 'creme',
    groupe: 'Voie cutanée',
    label: 'Crème / pommade',
    voieText: 'application sur la peau',
    unite: '',
    dosageType: 'text',
    dosageLabel: 'Quantité à appliquer',
    dosagePlaceholder: 'ex: une noisette, une fine couche...',
    showBoites: false,
    showSiteApplication: true,
  },
  {
    key: 'gel',
    groupe: 'Voie cutanée',
    label: 'Gel / lotion',
    voieText: 'application cutanée',
    unite: '',
    dosageType: 'text',
    dosageLabel: 'Quantité à appliquer',
    dosagePlaceholder: 'ex: une noisette, une fine couche...',
    showBoites: false,
    showSiteApplication: true,
  },
  {
    key: 'patch',
    groupe: 'Voie cutanée',
    label: 'Patch transdermique',
    voieText: 'appliqué sur la peau (diffusion progressive)',
    unite: 'patch',
    dosageType: 'count',
    dosageLabel: 'Nombre de patchs',
    showBoites: false,
    showSiteApplication: true,
  },
  {
    key: 'aerosol',
    groupe: 'Voie inhalée',
    label: 'Aérosol / inhalateur',
    voieText: 'inhalation',
    unite: 'bouffée',
    dosageType: 'count',
    dosageLabel: 'Nombre de bouffées',
    showBoites: false,
  },
  {
    key: 'injection',
    groupe: 'Voie injectable',
    label: 'Injection',
    voieText: 'injection',
    unite: 'dose',
    dosageType: 'text',
    dosageLabel: 'Dose',
    dosagePlaceholder: 'ex: 5 ml, 50 mg...',
    showBoites: false,
    showVoieInjection: true,
  },
  {
    key: 'suppositoire',
    groupe: 'Voie rectale',
    label: 'Suppositoire',
    voieText: 'voie rectale',
    unite: 'suppositoire',
    dosageType: 'count',
    dosageLabel: 'Nombre de suppositoires',
    showBoites: true,
  },
  {
    key: 'ovule',
    groupe: 'Voie vaginale',
    label: 'Ovule vaginal',
    voieText: 'voie vaginale',
    unite: 'ovule',
    dosageType: 'count',
    dosageLabel: "Nombre d'ovules",
    showBoites: true,
  },
  {
    key: 'gouttes',
    groupe: 'Voie ORL / ophtalmo',
    label: 'Gouttes',
    voieText: 'à instiller',
    unite: 'goutte',
    dosageType: 'count',
    dosageLabel: 'Nombre de gouttes',
    showBoites: false,
    showSiteGouttes: true,
  },
];

export const getVoie = (key: string): VoieMedicament =>
  VOIES_MEDICAMENT.find((v) => v.key === key) || VOIES_MEDICAMENT[0];

/**
 * Retourne le conditionnement par défaut pour une forme médicament donnée.
 * Comprimés, gélules, sublinguaux, pastilles → plaquette
 * Autres formes → boîte
 */
export const getDefaultConditionnement = (formeKey: string): Conditionnement => {
  const voie = getVoie(formeKey);
  return voie.defaultConditionnement || 'boite';
};

export const VOIES_GROUPES: { groupe: string; voies: VoieMedicament[] }[] =
  VOIES_MEDICAMENT.reduce<{ groupe: string; voies: VoieMedicament[] }[]>(
    (acc, voie) => {
      const existing = acc.find((g) => g.groupe === voie.groupe);
      if (existing) {
        existing.voies.push(voie);
      } else {
        acc.push({ groupe: voie.groupe, voies: [voie] });
      }
      return acc;
    },
    [],
  );

const pluralize = (unite: string, nb: string): string => {
  if (!unite || unite === 'ml') return unite;
  const num = parseInt(nb, 10);
  if (!Number.isNaN(num) && num > 1) return `${unite}s`;
  return unite;
};

const formatDose = (m: MedicamentLigne): string => {
  const voie = getVoie(m.forme);
  if (voie.dosageType === 'text') return m.dosageNb.trim();
  if (voie.dosageType === 'ml') return `${m.dosageNb} ml`;
  return `${m.dosageNb} ${pluralize(voie.unite, m.dosageNb)}`.trim();
};

export const doseCourte = (m: MedicamentLigne): string => {
  if (!m.dosageNb || !m.dosageNb.trim()) return '—';
  return formatDose(m);
};

const formatMoments = (m: MedicamentLigne): string =>
  [m.matin && 'Matin', m.midi && 'Midi', m.soir && 'Soir']
    .filter(Boolean)
    .join(', ');

const formatModalite = (m: MedicamentLigne): string => {
  const voie = getVoie(m.forme);
  if (m.forme === 'injection') {
    return m.voieInjection ? `voie ${m.voieInjection}` : voie.voieText;
  }
  if (m.forme === 'gouttes') {
    return m.siteGouttes ? `dans les ${m.siteGouttes}` : voie.voieText;
  }
  if (m.forme === 'poudre') {
    return m.dissolution || voie.voieText;
  }
  return voie.voieText;
};

export const formatConditionnement = (nb: string, conditionnement?: Conditionnement): string => {
  const unit = conditionnement === 'plaquette' ? 'plaquette' : 'boîte';
  const count = parseInt(nb, 10);
  const n = Number.isNaN(count) ? 1 : count;
  return n > 1 ? `${n} ${unit}s` : `${n} ${unit}`;
};

// Résumé sur une ligne (sauvegardé en base + affiché sous chaque médicament)
export const formatLigneOrdonnance = (m: MedicamentLigne): string => {
  const voie = getVoie(m.forme);
  const parts: string[] = [m.nom];
  const dose = formatDose(m);
  const moments = formatMoments(m);
  const modalite = formatModalite(m);
  if (dose) parts.push(dose);
  if (moments) parts.push(moments);
  if (modalite) parts.push(modalite);
  if (voie.showBoites) {
    parts.push(formatConditionnement(m.nbBoites, m.conditionnement));
  }
  if (m.siteApplication && m.siteApplication.trim()) {
    parts.push(`Sur : ${m.siteApplication.trim()}`);
  }
  if (m.duree && m.duree.trim()) {
    parts.push(`Durée : ${m.duree.trim()}`);
  }
  if (m.instructions && m.instructions.trim()) {
    parts.push(m.instructions.trim());
  }
  return parts.join(' — ');
};

// Ordonnance numérotée et aérée pour l'impression
export const formatOrdonnanceDocument = (medicaments: MedicamentLigne[]): string =>
  medicaments
    .filter((m) => m.nom.trim())
    .map((m, index) => {
      const voie = getVoie(m.forme);
      const dose = formatDose(m);
      const moments = formatMoments(m);
      const modalite = formatModalite(m);

      const lignes: string[] = [`${index + 1}. ${m.nom}`];
      const posoParts = [dose, moments, modalite].filter(Boolean);
      if (posoParts.length > 0) lignes.push(`   ${posoParts.join(' — ')}`);
      if (voie.showBoites) {
        lignes.push(`   ${formatConditionnement(m.nbBoites, m.conditionnement)}`);
      }
      if (m.siteApplication && m.siteApplication.trim()) {
        lignes.push(`   Sur : ${m.siteApplication.trim()}`);
      }
      if (m.duree && m.duree.trim()) {
        lignes.push(`   Durée : ${m.duree.trim()}`);
      }
      if (m.instructions && m.instructions.trim()) {
        lignes.push(`   ${m.instructions.trim()}`);
      }
      return lignes.join('\n');
    })
    .join('\n\n');